import { devMode } from "@src/env";
import type { Radio } from "@src/types";
import Echo, { Broadcaster } from "laravel-echo";
import Pusher from "pusher-js";

declare global {
  interface Window {
    Pusher: typeof Pusher;
    Echo: Echo<"reverb">;
  }
}

/**
 * ```json
 * {
 *   "id": 9,
 *   "title": "Teste 5",
 *   "created_at": "2025-08-01 11:50:30",
 *   "expires_at": "2025-08-02 11:50:30",
 *   "text": "<p>Hello World</p>",
 *   "image": null
 * }
 * ```
 */
export type TNotification = {
  id: number;
  title: string;
  created_at: string;
  expires_at: string;
  text?: string;
  image?: string;
};

export function initNotificationService(
  radio: Radio,
  onNotification: (notification: TNotification) => void,
  onConnect: () => void,
  onDisconnect: () => void,
  onConnecting: () => void,
): () => void {
  window.Pusher = Pusher;

  const options: Broadcaster["reverb"]["options"] = devMode
    ? {
        broadcaster: "reverb",
        key: "xte9aoxktsckqjer9evm", // ws://localhost:8081/app/xte9aoxktsckqjer9evm
        wsHost: "localhost",
        wsPort: 8081, // dev: php artisan reverb:start --debug + php artisan queue:work
        disableStats: true,
        forceTLS: false, // use ws:// instead of wss:// in dev
      }
    : {
        broadcaster: "reverb",
        key: "xte9aoxktsckqjer9evm", // wss://fabricca.pro/app/xte9aoxktsckqjer9evm
        wsHost: window.location.host,
        wssPort: 443, // prod
        disableStats: true,
        forceTLS: true, // use wss:// instead of ws:// in prod
      };
  window.Echo = new Echo<"reverb">(options);

  window.Echo.connector.pusher.connection.bind(
    "state_change",
    (state: { current: string; previous: string }) => {
      if (state.current === "connecting") {
        onConnecting();
      } else if (state.current === "connected") {
        onConnect();
      } else if (state.previous === "connected") {
        onDisconnect();
      } else {
        // console.warn("Unsupported WebSocket state change:", state);
      }
    },
  );

  const eventName = "NewNotification";
  const channelName = `App.Models.Radio.${radio.id}`;
  const channel = window.Echo.channel(channelName).listen(
    eventName,
    onNotification,
  );

  // setTimeout(() => {
  //   console.log("Connecting to WebSocket...", options);
  //   window.Echo.connector.connect();
  // }, 500);

  // cleanup function
  return () => {
    channel.stopListening(eventName, onNotification);
    window.Echo.leaveChannel(channelName);
    window.Echo.disconnect();
  };
}
